import { api } from './client';
import { compressImageIfNeeded } from '../../lib/compress-image';
import type {
  DocumentDownloadResponse,
  DocumentKind,
  DocumentResponse,
  DocumentUploadUrlResponse,
  Uuid,
} from '../types/api';

export interface RequestUploadUrlPayload {
  fileName: string;
  contentType: string;
  contentLength: number;
  kind: DocumentKind;
}

export async function requestDocumentUploadUrl(
  transactionId: Uuid,
  payload: RequestUploadUrlPayload,
): Promise<DocumentUploadUrlResponse> {
  const { data } = await api.post<DocumentUploadUrlResponse>(
    `/transactions/${transactionId}/documents/upload-url`,
    payload,
  );
  return data;
}

export interface ConfirmDocumentPayload {
  key: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  kind: DocumentKind;
}

export async function confirmDocument(
  transactionId: Uuid,
  payload: ConfirmDocumentPayload,
): Promise<DocumentResponse> {
  const { data } = await api.post<DocumentResponse>(
    `/transactions/${transactionId}/documents/confirm`,
    payload,
  );
  return data;
}

export async function getDocumentDownloadUrl(id: Uuid): Promise<DocumentDownloadResponse> {
  const { data } = await api.get<DocumentDownloadResponse>(`/documents/${id}/download`);
  return data;
}

export async function getDocumentFileDownloadUrl(id: Uuid): Promise<DocumentDownloadResponse> {
  const { data } = await api.get<DocumentDownloadResponse>(`/documents/${id}/download-file`);
  return data;
}

// End-to-end client-side upload: requests presigned URL, PUTs the file bytes
// direct to S3, confirms on the API. Returns the persisted Document row.
export async function uploadDocument(
  transactionId: Uuid,
  file: File,
  kind: DocumentKind,
): Promise<DocumentResponse> {
  let contentType = file.type || 'application/octet-stream';
  if (contentType.includes(';')) {
    contentType = contentType.split(';')[0].trim();
  }

  try {
    const presigned = await requestDocumentUploadUrl(transactionId, {
      fileName: file.name,
      contentType,
      contentLength: file.size,
      kind,
    });

    const headers: Record<string, string> = {};
    if (presigned.headers) {
      for (const [k, v] of Object.entries(presigned.headers)) {
        if (k.toLowerCase() !== 'content-length') {
          headers[k] = v;
        }
      }
    }
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = contentType;
    }

    const putRes = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
    });

    if (putRes.ok) {
      return await confirmDocument(transactionId, {
        key: presigned.key,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        kind,
      });
    }
  } catch (err) {
    console.warn('Presigned S3 upload failed, attempting direct upload fallback:', err);
  }

  // Fallback: Direct multipart upload through API server
  const processedFile = await compressImageIfNeeded(file);
  const formData = new FormData();
  formData.append('file', processedFile);
  formData.append('kind', kind);

  const { data } = await api.post<DocumentResponse>(
    `/transactions/${transactionId}/documents/upload-direct`,
    formData,
    { timeout: 120_000 },
  );
  return data;
}
